import { Alert, Checkbox, Divider, Form, message, Modal, Radio, Select, SelectProps, Tag } from "antd";
import { useEffect, useState } from "react";
import { useForm } from "antd/lib/form/Form";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthContext";
import { GroupService } from "../../Services/GroupService";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import { WebApiIntegrationsDtoExternalArticleDto, WebApiMemberDtoMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiModelsMembershipProduct } from "../../Repository/eventbookyapi";
import { BookyButton } from "../Buttons/BookyButton";
import { MemberTable } from "../Table/MemberTable";
import { MemberPaymentStatus } from "../../Types/MemberPaymentStatus";
import { BookySelectPaymentStatus } from "../Forms/BookyPaymentStatusSelect";
import { BookySelectDropDown } from "../Forms/BookySelectDropDown";
import { OneFrontDatePicker } from "../Forms/BookyDatePicker";
import { SpirisInvoiceRowsEditor, sumSpirisRows } from "../Forms/SpirisInvoiceRowsEditor";
import { IntegrationType } from "../../Types/IntegrationType";
import { formatKr } from "../../Helpers/FortnoxInvoiceHelpers";
import { PaymentConfiguration } from "../../Types/PaymentConfiguration";
import { EmailNotificationPreference } from "../../Types/EmailNotificationPreference";
import { CheckCircleFilled, CheckCircleOutlined } from "@ant-design/icons";
import { getPreviousCurrentAndNextYearAsOption } from "../../Helpers/PaymentYearOptions";
import { IntegrationService } from "../../Services/IntegrationService";
import { getPreviousYear } from "../../Helpers/DateHelpers";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { MemberFieldService } from "../../Services/MemberFieldService";
import { MembershipProductService } from "../../Services/MembershipProductService";
import { splitPrimariesAndFamily } from "../../Helpers/FamilyMemberHelper";

export const AddMultipleSpirisInvoicesForm = (props: any) => {
    const [memberFieldSettings, setMemberFieldSettings] = useState<any[]>([]);
    const [form] = useForm();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false as boolean);
    const [showConfirm, setShowConfirm] = useState(false as boolean);
    const [selectedGroup, setSelectedGroup] = useState([] as number[]);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(undefined as MemberPaymentStatus | undefined);

    const [isAllowedToSendGroupMails, setIsAllowedToSendGroupMails] = useState(true as boolean);
    const { user } = useAuth();
    const [selectedMembers, setSelectedMembers] = useState([] as WebApiMemberDtoMemberDto[]);
    const [hiddenFamilyCount, setHiddenFamilyCount] = useState(0 as number);
    const [invoiceMode, setInvoiceMode] = useState<"manual" | "automatic">("manual");
    const [membershipProducts, setMembershipProducts] = useState<WebApiModelsMembershipProduct[]>([]);
    const [articles, setArticles] = useState<WebApiIntegrationsDtoExternalArticleDto[]>([]);
    const [existingInvoiceMemberIds, setExistingInvoiceMemberIds] = useState<number[]>([]);
    const [linkedMemberIds, setLinkedMemberIds] = useState<number[]>([]);
    const [createMissingCustomers, setCreateMissingCustomers] = useState(false);
    const watchedRows = Form.useWatch('rows', form);
    const watchedInvoiceDate = Form.useWatch('invoiceDate', form);
    const watchedYear = Form.useWatch('year', form);

    const GetText = TextService.GetText;
    const hasMembershipProducts = membershipProducts.length > 0;
    const manualTotal = sumSpirisRows(watchedRows);

    // Which selected members already have a Spiris invoice for the chosen year - the batch job
    // skips these, so surface it upfront (mirrors the Fortnox flow).
    useEffect(() => {
        const year = watchedYear ?? form.getFieldValue("year");
        if (!year) return;
        IntegrationService.GetExistingSpirisInvoices(year.toString())
            .then(setExistingInvoiceMemberIds)
            .catch(() => setExistingInvoiceMemberIds([]));
    }, [watchedYear, form]);

    const selectedMembersWithExistingInvoice = () =>
        selectedMembers.filter(m => existingInvoiceMemberIds.includes(m.memberId!)).length;

    const selectedMembersNotInSpiris = () =>
        selectedMembers.filter(m => !linkedMemberIds.includes(m.memberId!)).length;

    // Members that would actually get an invoice: not already invoiced, and either in Spiris or opted in to create.
    const processableSelectedCount = () =>
        selectedMembers.filter(m =>
            !existingInvoiceMemberIds.includes(m.memberId!) &&
            (createMissingCustomers || linkedMemberIds.includes(m.memberId!))
        ).length;

    // Due date can't be before the invoice date (or today, if no invoice date is set).
    const disabledDueDate = (current: dayjs.Dayjs) => {
        if (!current) return false;
        const floor = watchedInvoiceDate ? dayjs(watchedInvoiceDate).startOf('day') : dayjs().startOf('day');
        return current.isBefore(floor);
    };

    useEffect(() => {
        MemberFieldService.GetMemberFields().then(setMemberFieldSettings);
        form.setFieldValue("year", getPreviousYear(0));
        form.setFieldValue("rows", [{}]);

        IntegrationService.GetExternalArticles(IntegrationType.Spiris)
            .then(setArticles)
            .catch(() => setArticles([]));

        IntegrationService.GetSpirisLinkedMembers()
            .then(setLinkedMemberIds)
            .catch(() => setLinkedMemberIds([]));

        if (props.groups != null && props.groups.length > 0) {
            setSelectedGroup(props.groups[0]);
            form.setFieldValue("group", props.groups[0].id)
        }

        MembershipProductService.GetAllMembershipProducts().then(products => {
            setMembershipProducts(products);
        }).catch(() => { });

        GroupService.IsAllowedToSendGroupMails().then(result => {
            setIsAllowedToSendGroupMails(result.toString() === true.toString());
        });
    }, [props]);

    const onCreateFinish = async () => {
        if (selectedMembers.length === 0) {
            message.warning(GetText("EmailForm_SelectOneGroup"));
            return;
        }

        if (invoiceMode === "manual") {
            try {
                await form.validateFields();
            } catch {
                return;
            }
        }

        setShowConfirm(true);
    };

    const onFilterMembers = async () => {
        let filteredMembers: WebApiMemberDtoMemberDto[] = [];

        if (!selectedGroup.length) {
            message.warning(GetText("EmailForm_SelectOneGroup"));
        }

        filteredMembers = await MemberService.GetFilteredMembers({
            status: selectedPaymentStatus,
            emailNotificationPreference: EmailNotificationPreference.Unspecified,
            isAllMembers: selectedGroup.length === 0 || selectedGroup.some(p => p === -1) ? true : false,
            groupsIds: selectedGroup,
        });

        // Family members share the household primary's billing — never invoice them directly.
        const { primaries, family } = splitPrimariesAndFamily(filteredMembers);
        setSelectedMembers(primaries);
        setHiddenFamilyCount(family.length);

        if (primaries.length === 0) {
            message.warning("No members found.")
        }
    }

    const onSendConfirmOK = async () => {
        setLoading(true);

        if (selectedMembers.length === 0) {
            message.error("Select at least one member");
            return;
        }

        var memberIds: number[] = selectedMembers.map(m => m.memberId!);

        try {
            const request: any = {
                memberIds: memberIds,
                connectedYear: form.getFieldValue("year").toString(),
                createMissingCustomers: createMissingCustomers,
            };

            if (invoiceMode === "automatic") {
                request.generateAmountFromMembership = true;
            } else {
                const rows = ((form.getFieldValue("rows") as any[]) ?? []).map(r => ({
                    description: (r.description ?? "").toString(),
                    amount: Number(r.amount) || 0,
                    articleNumber: r.articleNumber || undefined,
                }));
                request.rows = rows;
                request.description = rows[0]?.description ?? "";
                request.amount = rows[0]?.amount ?? 0;
            }

            // new Date(YYYY-MM-DD) parses as UTC midnight - avoids a one-day shift on toISOString().
            const dueDate = form.getFieldValue("dueDate");
            if (dueDate) request.dueDate = new Date(dayjs(dueDate).format("YYYY-MM-DD"));
            const invoiceDate = form.getFieldValue("invoiceDate");
            if (invoiceDate) request.invoiceDate = new Date(dayjs(invoiceDate).format("YYYY-MM-DD"));

            const response = await IntegrationService.CreateMultipleSpirisInvoices(request);

            if (response) {
                navigate(`/${getCurrentLanguage()}/batchJobStatus/${response}`);
            } else {
                message.success(GetText("AddMultipleSpirisInvoices_InvoicesCreated"));
            }
        } catch {
            message.error(GetText("AddMultipleSpirisInvoices_InvoicesFailedToCreate"))
        }

        setLoading(false);
        setShowConfirm(false);
    };

    const removeMember = (memberToRemove: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) => {
        let newList = selectedMembers.filter(p => p.memberId !== memberToRemove.memberId);
        setSelectedMembers(newList);
    };

    const groupOptions = () => {
        const options: SelectProps['options'] = [];

        props.groups.forEach((element: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            options.push({
                label: element.name,
                value: element.id,
            });
        });

        return options;
    }

    const handleGroupChange = async (value: number[]) => {
        setSelectedGroup(value);
    };

    const onSendConfirmCancel = async () => {
        setShowConfirm(false);
    };

    const onSelectPaymentChanged = async (value: MemberPaymentStatus) => {
        setSelectedPaymentStatus(value);
    };

    const onYearChanged = (value: any) => {
        form.setFieldValue("year", value);
    }

    const countMembersWithoutMembershipProduct = () => {
        return selectedMembers.filter(m => !m.membershipProduct).length;
    }

    return <>
        <Modal
            title={GetText("AddMultipleSpirisInvoices_ConfirmText")}
            open={showConfirm}
            onOk={onSendConfirmOK}
            confirmLoading={loading}
            onCancel={onSendConfirmCancel}
        >
            <p>{GetText("AddMultipleSpirisInvoices_ConfirmText1") + selectedMembers.length + GetText("AddMultipleSpirisInvoices_ConfirmText2") + " för " + form.getFieldValue("year")}</p>
        </Modal>
        {user?.hasPaidSubscription ?
            <>
                {isAllowedToSendGroupMails === true ? <Form
                    form={form}
                    layout="vertical"
                    onFinish={onCreateFinish}
                    autoComplete="off"
                >
                    <h3>{selectedMembers.length > 0 ? <CheckCircleFilled style={{ color: "green" }} /> : <CheckCircleOutlined style={{ color: "grey" }} />} {GetText("AddMultipleSpirisInvoices_Step1")}</h3>
                    <Divider style={{ marginTop: "5px" }} />
                    <span style={{ color: "red" }}>*</span> {GetText("AddMultipleSpirisInvoices_ChooseReceiver")}
                    {props.groups.length > 0 ? <><br />
                        <Select placeholder={GetText("EmailForm_ChooseCategories")} onChange={handleGroupChange} style={{ width: '400px', marginBottom: "10px" }} mode="multiple" options={groupOptions()}></Select>
                        <br /> {props.user?.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <BookySelectPaymentStatus showLastYearOptions={true} style={{ width: "400px" }} onSelectPaymentChanged={onSelectPaymentChanged} /> : ""}
                    </> : ""}

                    <br /><BookyButton style={{ marginTop: "10px" }} text={GetText("AddMultipleSpirisInvoices_CreateSendList")} onClick={onFilterMembers}></BookyButton>

                    {selectedMembers.length === 0 ? "" : <MemberTable user={user} isMail={true} members={selectedMembers} style={{ minWidth: '400px', marginTop: "10px" }} removeMember={removeMember} memberFieldSettings={memberFieldSettings} membershipProducts={membershipProducts}
                        showSpirisInvoiceColumn={true} spirisInvoiceColumnYear={watchedYear ?? form.getFieldValue("year")} spirisInvoiceMemberIds={existingInvoiceMemberIds}
                        showInSpirisColumn={true} inSpirisMemberIds={linkedMemberIds} />}
                    {selectedMembers.length > 0 ? <><br /><i>{selectedMembers.length} {GetText("AddMultipleSpirisInvoices_ChosenMembers")}</i></> : ""}

                    {hiddenFamilyCount > 0 && <Alert
                        message={GetText("AddMultiple_FamilyExcludedInfo").replace("{0}", hiddenFamilyCount.toString())}
                        type="info"
                        showIcon
                        style={{ marginTop: "10px", maxWidth: "600px" }}
                    />}

                    {selectedMembers.length > 0 && selectedMembersWithExistingInvoice() > 0 && <Alert
                        message={GetText("SpirisInvoice_ExistingInvoiceWarningTitle").replace("{0}", (watchedYear ?? form.getFieldValue("year"))?.toString())}
                        description={GetText("SpirisInvoice_ExistingInvoiceWarningDesc")
                            .replace("{0}", selectedMembersWithExistingInvoice().toString())
                            .replace("{1}", (watchedYear ?? form.getFieldValue("year"))?.toString())}
                        type="warning"
                        showIcon
                        style={{ marginTop: "10px", maxWidth: "600px" }}
                    />}

                    {selectedMembers.length > 0 && selectedMembersNotInSpiris() > 0 && <Alert
                        message={createMissingCustomers
                            ? GetText("SpirisInvoice_MissingCustomersCreateInfo").replace("{0}", selectedMembersNotInSpiris().toString())
                            : GetText("SpirisInvoice_MissingCustomersWarning").replace("{0}", selectedMembersNotInSpiris().toString())}
                        description={<Checkbox checked={createMissingCustomers} onChange={e => setCreateMissingCustomers(e.target.checked)}>
                            {GetText("SpirisInvoice_CreateMissingCustomersCheckbox")}
                        </Checkbox>}
                        type={createMissingCustomers ? "info" : "warning"}
                        showIcon
                        style={{ marginTop: "10px", maxWidth: "600px" }}
                    />}

                    {invoiceMode === "automatic" && selectedMembers.length > 0 && countMembersWithoutMembershipProduct() > 0 && <Alert
                        message={GetText("AddMultipleSpirisInvoices_AutomaticWarning").replace("{0}", countMembersWithoutMembershipProduct().toString())}
                        type="warning"
                        showIcon
                        style={{ marginTop: "15px", maxWidth: "600px" }}
                    />}

                    <h3 style={{ marginTop: "20px" }}>{GetText("AddMultipleSpirisInvoices_Step2")}</h3>
                    <Divider style={{ margin: "5px" }} />

                    {hasMembershipProducts && (
                        <Form.Item label={GetText("AddMultipleSpirisInvoices_InvoiceMode")}>
                            <Radio.Group value={invoiceMode} onChange={(e) => setInvoiceMode(e.target.value)}>
                                <Radio value="manual">{GetText("AddMultipleSpirisInvoices_ModeManual")}</Radio>
                                <Radio value="automatic">{GetText("AddMultipleSpirisInvoices_ModeAutomatic")}</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}

                    {invoiceMode === "manual" && (
                        <>
                            <SpirisInvoiceRowsEditor form={form} articles={articles} />
                            <div style={{ color: '#666', fontSize: 13, margin: '0 0 12px' }}>
                                <strong>{GetText("SpirisInvoice_SummaryTotal")}: {formatKr(manualTotal)}</strong>
                            </div>
                        </>
                    )}
                    <BookySelectDropDown style={{ width: '160px' }} label={GetText("SpirisInvoice_MembershipYearLabel")} allowClear={false} name="year" handleChange={onYearChanged} options={getPreviousCurrentAndNextYearAsOption()} help={GetText("SpirisInvoice_MembershipYearTooltip")} helpTitle={GetText("SpirisInvoice_MembershipYearLabel")} />
                    <OneFrontDatePicker label={GetText("SpirisInvoice_InvoiceDateLabel")} name="invoiceDate" placeholder={GetText("SpirisInvoice_InvoiceDatePlaceholder")} tooltip={GetText("SpirisInvoice_InvoiceDateTooltip")} />
                    <OneFrontDatePicker label={GetText("SpirisInvoice_DueDateLabel")} name="dueDate" placeholder={GetText("SpirisInvoice_DueDatePlaceholder")} disabledDate={disabledDueDate} />
                    <Form.Item label={" "}>
                        <BookyButton
                            type="primary"
                            fullWidth={false}
                            text={GetText("AddMultipleSpirisInvoices_CreateButton")}
                            onClick={onCreateFinish}
                            disabled={selectedMembers.length === 0 || processableSelectedCount() === 0}
                        >
                        </BookyButton>
                        {selectedMembers.length > 0 && processableSelectedCount() === 0 &&
                            <div style={{ color: '#cf1322', fontSize: 13, marginTop: 6 }}>
                                {selectedMembersWithExistingInvoice() === selectedMembers.length
                                    ? GetText("SpirisInvoice_AllHaveInvoices").replace("{0}", (watchedYear ?? form.getFieldValue("year"))?.toString())
                                    : GetText("SpirisInvoice_NoneProcessable")}
                            </div>}
                    </Form.Item>
                </Form> : <Alert
                    message={GetText("EmailForm_AlertSuspendedMessage")}
                    description={GetText("EmailForm_AlertSuspendedDescription")}
                    type="warning"
                />}
            </> : <span><Tag color="#1890ff">{GetText("Common_Subscription")}</Tag><a href={"/" + getCurrentLanguage() + "/Pricing"}>{GetText("Common_UpgradeToPremium")}</a>{GetText("EmailForm_PremiumSendingMessageToGroups")}</span>
        }

    </>
}
